import { Button } from '@/components/common';
import { RESPONSES } from '@/constants';
import { ReferidoNavigator } from './ReferidoNavigator';
import type { ReferidoFormData, ResponseType } from '@/types';

interface PhaseResponsesProps {
  referidos: ReferidoFormData[];
  currentIndex: number;
  onUpdateReferido: (index: number, updates: Partial<ReferidoFormData>) => void;
  onNavigate: (index: number) => void;
  onComplete: () => void;
  onBack: () => void;
}

const RESPONSE_COLORS: Record<ResponseType, string> = {
  OK_GRACIAS: 'border-green-500 bg-green-50 text-green-700',
  PREGUNTO_QUIEN_ES: 'border-yellow-500 bg-yellow-50 text-yellow-700',
  NO_RESPONDIO: 'border-gray-300 bg-gray-50 text-gray-600',
  CITA_INSTANTANEA: 'border-blue-500 bg-blue-50 text-blue-700',
};

const RESPONSE_BADGE: Record<ResponseType, string> = {
  OK_GRACIAS: 'bg-green-100 text-green-700',
  PREGUNTO_QUIEN_ES: 'bg-yellow-100 text-yellow-700',
  NO_RESPONDIO: 'bg-gray-100 text-gray-600',
  CITA_INSTANTANEA: 'bg-blue-100 text-blue-700',
};

const RESPONSE_SHORT: Record<ResponseType, string> = {
  OK_GRACIAS: 'OK ✅',
  PREGUNTO_QUIEN_ES: 'Preguntó 🤔',
  NO_RESPONDIO: 'Sin respuesta ⏳',
  CITA_INSTANTANEA: 'Cita 🤝',
};

export function PhaseResponses({
  referidos,
  currentIndex,
  onUpdateReferido,
  onNavigate,
  onComplete,
  onBack,
}: PhaseResponsesProps) {
  const current = referidos[currentIndex];

  if (!current) return null;

  const filledCount = referidos.filter((r) => r.respuesta_referido).length;
  const displayName = current.nombre + (current.apellido ? ` ${current.apellido}` : '');

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Respuesta de referidos
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          ¿Cómo respondió cada referido al mensaje?{' '}
          {filledCount}/{referidos.length} completados
        </p>
      </div>

      {/* Navigator */}
      <ReferidoNavigator
        currentIndex={currentIndex}
        total={referidos.length}
        onPrev={() => onNavigate(currentIndex - 1)}
        onNext={() => onNavigate(currentIndex + 1)}
        currentName={displayName}
      />

      {/* Show message type badge */}
      {current.mensaje_enviado && (
        <div className="flex items-center gap-2 px-1">
          <span className="text-xs text-gray-500">Mensaje enviado por:</span>
          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">
            {current.mensaje_enviado === 'WHATSAPP'
              ? 'WhatsApp'
              : current.mensaje_enviado === 'IMESSAGE'
                ? 'iMessage'
                : current.mensaje_enviado === 'CITA_INSTANTANEA'
                  ? 'Llamada instantánea'
                  : 'SMS'}
          </span>
        </div>
      )}

      {/* Response selector */}
      <div className="space-y-2">
        {RESPONSES.map((resp) => (
          <button
            key={resp.value}
            type="button"
            onClick={() =>
              onUpdateReferido(currentIndex, {
                respuesta_referido: resp.value as ResponseType,
              })
            }
            className={`w-full px-4 py-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${current.respuesta_referido === resp.value
                ? RESPONSE_COLORS[resp.value as ResponseType]
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
          >
            {resp.label}
          </button>
        ))}
      </div>

      {/* Mini list showing all referidos response status */}
      <div className="space-y-1">
        {referidos.map((ref, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onNavigate(idx)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${idx === currentIndex
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'hover:bg-gray-50 text-gray-600'
              }`}
          >
            <span>{ref.nombre}{ref.apellido ? ` ${ref.apellido}` : ''}</span>
            {ref.respuesta_referido ? (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${RESPONSE_BADGE[ref.respuesta_referido]}`}>
                {RESPONSE_SHORT[ref.respuesta_referido]}
              </span>
            ) : (
              <span className="text-xs text-gray-400">pendiente</span>
            )}
          </button>
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-3 pt-2">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          Volver
        </Button>
        <Button onClick={onComplete} className="flex-1">
          Continuar a Firma
        </Button>
      </div>
    </div>
  );
}
